
import { defineStore } from 'pinia'
import { fetchWrapper } from '@/helpers/fetchWrapper';
import { cloneDeep, isEqual } from "lodash";

import moment from 'moment'
import { watch } from 'vue';
import { filterState } from '../helpers/FilterWrapper';
import { useRoute } from 'vue-router';
import { stringify, v4 as uuidv4 } from 'uuid';
import { useUserStore } from './user';
import { useSurveyAnswersStore } from './surveyAnswers';
import { useFileStore } from './file';
import { useAppStore } from './app';

const initialState = {
  assetGrades: [],
}

const BULK_SYNC_WINDOW_DAYS = 90;

// initByRoute (and so syncByAssetId) fires on every route change / re-render,
// but historic grades are read-only, so backfilling an asset once per session
// is enough. Track which ids we've already fetched to stop the re-fetch loop.
const backfilledAssetIds = new Set();

export const useAssetGradesStore = defineStore('assetGrades', {
  persistence: true,
  state: () => (initialState),
  getters: {
    getAssetGradeById: (state) => {
      return (assetGradeId) => {
        return state.assetGrades.find((assetGrade) => assetGrade.id === assetGradeId)
      }
    },
    getAssetGradesByInspectionId: (state) => {
      return (inpsectionId) => {
        return state.assetGrades.filter((assetGrade) => assetGrade.id === inspectionId)
      }
    },
    getAssetGradesByInspectionIdAndAssetId: (state) => {
      return (inspectionId, assetId) => {
        return filterState(state.assetGrades.filter((assetGrade) => (assetGrade.inspection_id == inspectionId) && (assetGrade.asset_id == assetId))).deletedFilter().get()
      }
    },
    getAssetGradesByInspectionIdAndAssetIdWithAnswers: (state) => {
      return (inspectionId, assetId) => {
        return filterState(state.assetGrades.filter((assetGrade) => (assetGrade.inspection_id == inspectionId) && (assetGrade.asset_id == assetId))).deletedFilter().has('answers').get()
      }
    },
    getAssetGradesByInspectionIdAndAssetIdAsc: (state) => {
      return (inspectionId, assetId) => {
        return filterState(state.assetGrades.filter((assetGrade) => (assetGrade.inspection_id == inspectionId) && (assetGrade.asset_id == assetId))).sortAsc('graded_at').get()
      }
    },
    getAssetGrades: (state) => {
      return (fuzzyText, dateStatus) => {
        const route = useRoute()
        return filterState(state.assetGrades).deletedFilter().dateToFilter(dateStatus).inpsectionFilter(route.params?.site_id).fuzzyFilter(fuzzyText).get();
      }
    },
    getAssetGradesBySiteId: (state) => {
      return (siteId) => {
        return filterState(state.assetGrades).deletedFilter().assetGradesBySiteIdFilter(siteId).get();
      }
    },
    getAssetGradesBySiteIdWithoutInspectionsAsc: (state) => {
      return (siteId) => {
        return filterState(state.assetGrades.filter((assetGrade) => (assetGrade.inspection_id === null)))
          .deletedFilter()
          .assetGradesBySiteIdFilter(siteId)
          .sortAsc('graded_at')
          .get()
      }
    },
    getAssetGradesByAssetTypeId: (state) => {
      return (siteId, assetTypeId) => {
        return filterState(state.assetGrades).assetGradesBySiteIdFilter(siteId).assetGradesByAssetTypeId(assetTypeId).deletedFilter().get();
      }
    },
    getLatestAssetGradeForAssetId: (state) => {
      return (assetId) => {
        return filterState(state.assetGrades).deletedFilter().where('asset_id', assetId).sortDesc('graded_at').first();
      }
    },
    getLatestAssetGradeWithAnswersForAssetId: (state) => {

      return (assetId) => {
        return filterState(state.assetGrades).deletedFilter().where('asset_id', assetId).has('answers').sortDesc('graded_at').first();
      }
    },
    getLatestAssetGradeWithAnswersForAssetIds: (state) => {

      return (assetIds) => {
        return filterState(state.assetGrades).deletedFilter().whereIn('asset_id', assetIds).has('answers').sortDesc('graded_at').get();
      }
    },
    getAssetGradesByAssetId: (state) => {
      return (assetId) => {
        return filterState(state.assetGrades).deletedFilter().where('asset_id', assetId).sortDesc('graded_at').get();
      }
    },
  },
  actions: {
    async sync() {
      // Send everything the client has, including older on-demand grades. The
      // backend only limits NEW grades to the 90-day window (newDataScope); it
      // reconciles stored ids against the full table, so old grades are
      // recognised as existing and not re-fetched or demanded back.
      const stored = this.assetGrades
        .map((assetGrade) => ({ id: assetGrade.id, updated_at: assetGrade.updated_at }));

      await fetchWrapper.sync(`/asset-grades/index`, `/asset-grades/update-or-create`, {
        stored,
      }, this.assetGrades);
    },

    // Bulk sync only carries current/future asset grades; this fetches a
    // single inspection's grades (current, future, or past) in the background,
    // e.g. when the user opens that inspection.
    async syncByInspectionId(inspectionId) {
      const stored = this.assetGrades
        .filter((assetGrade) => assetGrade.inspection_id === inspectionId)
        .map((assetGrade) => ({ id: assetGrade.id, updated_at: assetGrade.updated_at }));

      await fetchWrapper.sync(`/asset-grades/by-inspection/${inspectionId}`, `/asset-grades/update-or-create`, {
        stored,
      }, this.assetGrades);
    },

    // Bulk sync only carries current/future asset grades; this fetches a single
    // asset's grades (including past ones) in the background, e.g. when the user
    // searches/opens that asset directly.
    async syncByAssetId(assetId) {
      if (!assetId || backfilledAssetIds.has(assetId)) return;
      backfilledAssetIds.add(assetId);

      const stored = this.assetGrades
        .filter((assetGrade) => assetGrade.asset_id === assetId)
        .map((assetGrade) => ({ id: assetGrade.id, updated_at: assetGrade.updated_at }));

      await fetchWrapper.sync(`/asset-grades/by-asset/${assetId}`, `/asset-grades/update-or-create`, {
        stored,
      }, this.assetGrades);
    },

    // Drops cached asset grades for inspections that ended more than `ttlDays`
    // ago, since the bulk sync stops refreshing them once they fall outside its
    // 90-day window. Skips the inspection currently being viewed so it doesn't
    // vanish mid-view.
    //
    // The cutoff MUST match the backend getAll/newDataScope window (midnight -
    // ttlDays, inclusive) exactly. If it's even slightly stricter, grades right
    // at the boundary get fetched by bulk sync then immediately pruned here,
    // refetched next cycle - an endless new_data loop.
    pruneExpiredAssetGrades(ttlDays = BULK_SYNC_WINDOW_DAYS, activeInspectionId = null) {
      const cutoff = moment().startOf('day').subtract(ttlDays, 'days');

      const kept = this.assetGrades.filter((assetGrade) => {
        if (!assetGrade.inspection_id || assetGrade.inspection_id === activeInspectionId) return true;
        return !assetGrade.inspection_date_to || !moment(assetGrade.inspection_date_to).isBefore(cutoff);
      });

      // Mutate in place - do NOT reassign this.assetGrades. This runs
      // synchronously right after sync() fires its request, and fetchWrapper
      // holds a reference to the current array to push the response into later.
      // Reassigning would orphan that array, so the fetched grades would never
      // land in the store or get persisted to IndexedDB. Only touch it when
      // something actually needs removing, to avoid needless writes.
      if (kept.length !== this.assetGrades.length) {
        this.assetGrades.splice(0, this.assetGrades.length, ...kept);
      }
    },

    saveAssetGrade(assetGrade) {
      let assetGradeIndex = this.$state.assetGrades.findIndex((assetGradeToFind) => assetGradeToFind.id === assetGrade.id)
      const user = useUserStore()
      const surveryAnswers = useSurveyAnswersStore()
      const fileStore = useFileStore()
      assetGrade.created_by_id = assetGrade.created_by_id ? assetGrade.created_by_id : user.$state.user.id
      assetGrade.updated_at = moment().format()

      surveryAnswers.$state.answers.forEach((answer) => {
        answer.asset_grade_id = assetGrade.id

        if (File.prototype.isPrototypeOf(answer.value)) {
          // fileStore.storeFile(answer.value, answer)
        }
        else {
          try {
            JSON.parse(answer.value);
            answer.value = answer.value
          }
          catch (e) {
            answer.value = JSON.stringify(answer.value)
          }
        }

      })
      assetGrade.answers = surveryAnswers.$state.answers
      if (this.assetGrades[assetGradeIndex]) {
        this.assetGrades[assetGradeIndex] = assetGrade
        const appStore = useAppStore();
        appStore.setSnackbar("Successfully updated Asset Grade")
      } else {
        this.assetGrades.push(assetGrade)
        const appStore = useAppStore();
        appStore.setSnackbar("Successfully created Asset Grade")
      }
    },
    clonePrevious(asset) {
      const assetGrade = cloneDeep(this.getLatestAssetGradeForAssetId(asset.id.value))
      assetGrade.id = uuidv4()
      assetGrade.graded_at = moment().format()
      assetGrade.answers.forEach(answer => {
        answer.id = uuidv4()
      });
      const appStore = useAppStore();
      appStore.setSnackbar("Survey successfully saved")

      this.assetGrades.push(assetGrade)
    },

    latestAssetGradeForUniqueAssets(siteId, assetTypeId) {
      const assetGrades = this.getAssetGradesByAssetTypeId(siteId, assetTypeId);

      // Create a Map to track the latest asset grade for each asset ID
      const latestAssetGrades = new Map();

      assetGrades.forEach(assetGrade => {
        const existingGrade = latestAssetGrades.get(assetGrade.asset_id);
        if (!existingGrade || moment(assetGrade.graded_at).isAfter(existingGrade.graded_at)) {
          latestAssetGrades.set(assetGrade.asset_id, assetGrade);
        }
      });

      // Convert the Map values to an array and return
      return Array.from(latestAssetGrades.values());
    },
    deleteById(id) {
      const index = this.$state.assetGrades.findIndex((asset_grade) => asset_grade.id == id)
      this.$state.assetGrades[index].updated_at = moment().format()
      this.$state.assetGrades[index].deleted_at = moment().format()
      this.$state.deep_rerender = uuidv4()
      const appStore = useAppStore()
      const vm = this
      this.router.push({ name: 'Asset-Overview', params: { asset_id: this.$state.assetGrades[index].asset_id } })
      appStore.setSnackbar("Successfully deleted grading", function () {

        vm.$state.assetGrades[index].updated_at = moment().format()
        vm.$state.assetGrades[index].deleted_at = null
        vm.$state.deep_rerender = uuidv4()
        vm.router.push({ name: 'Asset-Overview', params: { asset_id: vm.$state.assetGrades[index].asset_id } })
      })

    }
  }
})
